import React, { JSX, useState, useMemo } from 'react';
import { Edit, Visibility, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useAuth } from 'hooks/AuthContext';
import * as Types from 'types/types';
import { useAppState } from 'hooks/GlobalContext';
import { DeleteConfirmation } from './DeleteConfirmation';
import { DocumentView } from './DocumentView';

interface DocumentListProps {
  documents: Types.Document[];
  setDocuments: (documents: Types.Document[]) => void;
  setSelectedDocument?: (document: Types.Document | null) => void;
  selectedDocument?: Types.Document | null;
}

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileTypeColor = (theme: Theme, type: string): string => {
  switch (type) {
    case 'pdf':
      return theme.palette.primary.main;
    case 'docx':
      return theme.palette.secondary.main;
    case 'txt':
      return theme.palette.success.main;
    case 'md':
      return theme.palette.warning.main;
    default:
      return theme.palette.primary.main;
  }
};

type SortField = 'name' | 'date' | 'size' | 'type' | 'rag';

const DocumentList = (props: DocumentListProps): JSX.Element => {
  const theme = useTheme();
  const { setSnack } = useAppState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { apiClient } = useAuth();
  const { documents, setDocuments, setSelectedDocument, selectedDocument } = props;
  const [documentView, setDocumentView] = useState<Types.Document | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [sortField, setSortField] = useState<SortField | null>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Sort documents
  const sortedDocuments = useMemo(() => {
    if (!sortField) return documents;

    return [...documents].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'name':
          aValue = a.filename.toLowerCase();
          bValue = b.filename.toLowerCase();
          break;
        case 'date':
          aValue = (a.updatedAt || a.uploadDate)?.getTime() || 0;
          bValue = (b.updatedAt || b.uploadDate)?.getTime() || 0;
          break;
        case 'size':
          aValue = a.size;
          bValue = b.size;
          break;
        case 'type':
          aValue = a.type.toLowerCase();
          bValue = b.type.toLowerCase();
          break;
        case 'rag':
          aValue = a.options?.includeInRag ? 1 : 0;
          bValue = b.options?.includeInRag ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [documents, sortField, sortDirection]);

  // Handle sorting
  const handleSort = (field: SortField): void => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort icon
  const renderSortIcon = (field: SortField): JSX.Element | null => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} />
    ) : (
      <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
    );
  };

  // Start rename process
  const viewDocument = (document: Types.Document, edit = false): void => {
    console.log('Starting rename for document:', document, document.filename);
    setDocumentView(document);
    setIsEditing(edit);
  };

  // Handle document deletion
  const handleDeleteDocument = async (document: Types.Document): Promise<void> => {
    try {
      // Call API to delete document
      await apiClient.deleteCandidateDocument(document);

      setDocuments(documents.filter(doc => doc.id !== document.id));
      setSnack('Document deleted successfully', 'success');

      // Close content view if this document was being viewed
      if (selectedDocument?.id === document.id) {
        setSelectedDocument && setSelectedDocument(null);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Handle RAG flag toggle
  const handleRAGToggle = async (
    document: Types.Document,
    includeInRag: boolean
  ): Promise<void> => {
    try {
      document.options = { includeInRag };
      // Call API to update RAG flag
      await apiClient.updateCandidateDocument(document);

      setDocuments(
        documents.map(doc => (doc.id === document.id ? { ...doc, options: { includeInRag } } : doc))
      );
      setSnack(`Document ${includeInRag ? 'included in' : 'excluded from'} RAG`, 'success');
    } catch (error) {
      setSnack('Failed to update RAG setting', 'error');
    }
  };

  return (
    <Box
      className="DocumentList"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        p: 0,
        m: 0,
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
                onClick={() => handleSort('name')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Name
                  {renderSortIcon('name')}
                </Box>
              </TableCell>
              {!isMobile && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: '80px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => handleSort('type')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Type
                    {renderSortIcon('type')}
                  </Box>
                </TableCell>
              )}
              {!isMobile && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: '80px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => handleSort('size')}
                >
                  Size
                  {renderSortIcon('size')}
                </TableCell>
              )}
              {!isMobile && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: '100px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => handleSort('date')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date
                    {renderSortIcon('date')}
                  </Box>
                </TableCell>
              )}
              <TableCell
                sx={{
                  fontWeight: 600,
                  width: '80px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
                onClick={() => handleSort('rag')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  RAG
                  {renderSortIcon('rag')}
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: '120px', textAlign: 'center' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDocuments.map(doc => (
              <TableRow
                key={doc.id}
                hover
                sx={{
                  backgroundColor:
                    selectedDocument?.id === doc.id ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor:
                      selectedDocument?.id === doc.id
                        ? 'rgba(0, 0, 0, 0.25) !important' // Slightly darker when selected + hover
                        : 'action.hover !important',
                  },
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSelectedDocument &&
                    setSelectedDocument(selectedDocument?.id === doc.id ? null : doc);
                }}
              >
                <TableCell sx={{ py: 1, maxWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      title={doc.filename}
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '0.875rem',
                        minWidth: 0,
                        flex: '1 1 auto',
                      }}
                    >
                      {doc.filename}
                    </Typography>
                    {isMobile && (
                      <Box
                        sx={{
                          display: 'flex',
                          width: 'fit-content',
                          flexShrink: 0,
                          border: `1px solid ${getFileTypeColor(theme, doc.type)}`,
                          height: 20,
                          px: 1,
                          fontSize: '0.65rem',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {doc.type.toUpperCase()}
                      </Box>
                    )}
                    {isMobile && (
                      <Box sx={{ width: '100%', mt: 0.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {formatFileSize(doc.size)} •{' '}
                          {(doc?.updatedAt || doc?.uploadDate)?.toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                {!isMobile && (
                  <TableCell sx={{ py: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        width: 'fit-content',
                        border: `1px solid ${getFileTypeColor(theme, doc.type)}`,
                        height: 20,
                        px: 1,
                        fontSize: '0.65rem',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {doc.type.toUpperCase()}
                    </Box>
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(doc.size)}
                    </Typography>
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {(doc?.updatedAt || doc?.uploadDate)?.toLocaleDateString()}
                    </Typography>
                  </TableCell>
                )}
                <TableCell sx={{ py: 1, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                  <Chip
                    label="RAG"
                    size="small"
                    color="success"
                    variant={doc.options?.includeInRag ? 'filled' : 'outlined'}
                    onClick={e => {
                      e.stopPropagation();
                      handleRAGToggle(doc, !doc.options?.includeInRag);
                    }}
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ py: 1, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                    <IconButton
                      size="small"
                      onClick={(e): void => {
                        e.stopPropagation();
                        viewDocument(doc);
                      }}
                      title="View content"
                      sx={{ p: 0.5 }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e): void => {
                        e.stopPropagation();
                        viewDocument(doc, true);
                      }}
                      title="Rename"
                      sx={{ p: 0.5 }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <DeleteConfirmation
                      onDelete={(): void => {
                        handleDeleteDocument(doc);
                      }}
                      // color="primary"
                      sx={{ minWidth: 'auto', maxHeight: 'min-content' }}
                      size="small"
                      action="delete"
                      label="this document"
                      title="Delete document"
                      message={`Are you sure you want to delete this document? This action cannot be undone.`}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Rename Dialog */}
      <Dialog
        open={documentView !== null}
        onClose={(): void => {
          setDocumentView(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        {documentView && (
          <DocumentView
            document={documentView}
            edit={isEditing}
            onSave={() => {
              setDocumentView(null);
              setIsEditing(false);
              setDocuments([...documents]);
            }}
            onCancel={() => {
              setDocumentView(null);
            }}
          />
        )}
      </Dialog>
    </Box>
  );
};

export { DocumentList };
